import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getClientMeasurementHistory } from '../../../services/metrics.service';
import { MeasurementHistoryItem } from '../../../types/metrics';

interface ClientMeasurementHistoryProps {
  clientId: string;
  onDataChange: () => void;
}

export const ClientMeasurementHistory: React.FC<ClientMeasurementHistoryProps> = ({ clientId }) => {
  const [history, setHistory] = useState<MeasurementHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClientMeasurementHistory(clientId, { limit: 100 });
      setHistory(data);
    } catch (err) {
      setError('Не вдалося завантажити історію вимірювань клієнта.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderTable = () => {
    if (loading) {
      return <Box display="flex" justifyContent="center" sx={{ my: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Тип</TableCell>
              <TableCell align="right">Значення</TableCell>
              <TableCell>Дата</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Історія вимірювань порожня.
                </TableCell>
              </TableRow>
            ) : (
              history.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.measurementType.name}</TableCell>
                  <TableCell align="right">{`${item.value} ${item.unit.symbol}`}</TableCell>
                  <TableCell>{formatDate(item.measuredAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Історія вимірювань</Typography>
      {renderTable()}
    </Box>
  );
};
